import React, { useState, useRef } from "react";
import api from "../lib/api";
import { PageTitle } from "../components/Layout";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { ICONS_BY_NAME } from "../lib/icons";
import { PasswordRules, isValidPassword } from "./ResetPassword";
import AuthImg from "../components/AuthImg";

const socialUrl = (platform, val) => {
  if (!val) return null;
  const v = val.trim();
  if (/^https?:\/\//i.test(v)) return v;
  const h = v.replace(/^@/, "");
  const base = { instagram: "https://instagram.com/", linkedin: "https://linkedin.com/in/", facebook: "https://facebook.com/", tiktok: "https://tiktok.com/@", youtube: "https://youtube.com/@" }[platform];
  return base ? base + h : (v.includes(".") ? `https://${v}` : null);
};

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const isCollab = user.role === "collaborator";
  const avatarRef = useRef();
  const [name, setName] = useState(user.name || "");
  const [fn, setFn] = useState(user.first_name || "");
  const [ln, setLn] = useState(user.last_name || "");
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);
  const [editP, setEditP] = useState(false);
  const [savingP, setSavingP] = useState(false);
  const [c, setC] = useState({
    email: user.email || "", phone: user.phone || "", address: user.address || "",
    country: user.country || "", city: user.city || "", travel_radius: user.travel_radius ?? "",
  });
  const s0 = user.socials || {};
  const [socials, setSocials] = useState({
    instagram: s0.instagram || "", linkedin: s0.linkedin || "", facebook: s0.facebook || "",
    tiktok: s0.tiktok || "", youtube: s0.youtube || "", other_label: s0.other_label || "", other_value: s0.other_value || "",
  });

  const startEditP = () => {
    setName(user.name || "");
    setFn(user.first_name || "");
    setLn(user.last_name || "");
    setC({
      email: user.email || "", phone: user.phone || "", address: user.address || "",
      country: user.country || "", city: user.city || "", travel_radius: user.travel_radius ?? "",
    });
    const us = user.socials || {};
    setSocials({
      instagram: us.instagram || "", linkedin: us.linkedin || "", facebook: us.facebook || "",
      tiktok: us.tiktok || "", youtube: us.youtube || "", other_label: us.other_label || "", other_value: us.other_value || "",
    });
    setEditP(true);
  };

  const save = async () => {
    let displayName = name.trim();
    if (isCollab) {
      if (!fn.trim() && !ln.trim()) return toast.error("Le prénom ou le nom est obligatoire");
      displayName = `${fn.trim()} ${ln.trim()}`.trim();
    } else if (!name.trim()) return toast.error("Le nom est obligatoire");
    if (isCollab && !c.country.trim()) return toast.error("Le pays est obligatoire");
    const payload = { name: displayName, phone: c.phone, address: c.address };
    if (c.email.trim()) payload.email = c.email.trim();
    if (isCollab) {
      payload.first_name = fn.trim();
      payload.last_name = ln.trim();
      payload.country = c.country.trim();
      payload.city = c.city.trim();
      payload.travel_radius = c.travel_radius === "" ? null : parseInt(c.travel_radius) || 0;
      payload.socials = socials;
    }
    setSavingP(true);
    try {
      const { data } = await api.put("/me/profile", payload);
      setUser(data);
      toast.success("Profil mis à jour");
      setEditP(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Échec de l'enregistrement"); }
    finally { setSavingP(false); }
  };

  const submitPwd = async (e) => {
    e.preventDefault();
    if (!isValidPassword(pwd.new_password)) return toast.error("Le mot de passe doit contenir au moins 8 caractères, dont une majuscule et un symbole.");
    if (pwd.new_password !== pwd.confirm) return toast.error("Les mots de passe ne correspondent pas");
    setSavingPwd(true);
    try {
      await api.post("/me/password", { current_password: pwd.current_password, new_password: pwd.new_password });
      toast.success("Mot de passe mis à jour");
      setPwd({ current_password: "", new_password: "", confirm: "" });
    } catch (e2) { toast.error(e2.response?.data?.detail || "Échec de la mise à jour"); }
    finally { setSavingPwd(false); }
  };

  const uploadAvatar = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/me/avatar", fd); setUser({ ...user, avatar_url: data.avatar_url }); toast.success("Photo mise à jour"); }
    catch { toast.error("Échec du téléversement"); }
  };

  // « Télécharger mes données » du collaborateur (décision du 15/10, question 10), comme « Mon compte » de l'entreprise.
  const [exporting, setExporting] = useState(false);
  const exportData = async () => {
    setExporting(true);
    try {
      const r = await api.get("/collab/account/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `mes-donnees-metara-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
    } catch { toast.error("Échec de l'export"); }
    finally { setExporting(false); }
  };

  return (
    <div className="max-w-3xl">
      <PageTitle overline="Mon espace" title="Profil utilisateur" />

      <div className="flex items-center gap-4 mb-8">
        <div className="relative">
          <div className="w-16 h-16 rounded-full bg-marine text-white flex items-center justify-center font-display text-2xl overflow-hidden">
            {user.avatar_url ? <AuthImg path={user.avatar_url} alt={user.name} className="w-full h-full object-cover" /> : user.name?.[0]?.toUpperCase()}
          </div>
          <button aria-label="Changer ma photo" onClick={() => avatarRef.current.click()} data-testid="profile-avatar-upload" className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-bordeaux text-white flex items-center justify-center border-2 border-creme hover:bg-bordeaux-hover transition-colors"><Icons.Camera size={16} /></button>
          <input ref={avatarRef} type="file" accept="image/*" className="hidden" onChange={uploadAvatar} />
        </div>
        <div>
          {user.job_role && <div className="text-xs uppercase tracking-[0.2em] font-semibold text-bordeaux mb-1">{user.job_role}</div>}
          <div className="font-display text-2xl text-marine">{user.name}</div>
        </div>
      </div>

      <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-6 mb-8" data-testid="profile-coordonnees">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl text-marine">Coordonnées</h2>
          {!editP && <button onClick={startEditP} data-testid="profile-edit-btn" className="text-sm border border-marine/25 text-marine rounded-sm px-3 py-1.5 hover:bg-marine hover:text-white transition-colors flex items-center gap-1.5"><Icons.Pencil size={16} /> Modifier</button>}
        </div>
        {!editP ? (
          <>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
              <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Nom</div><div className="text-marine font-medium">{isCollab ? ([fn, ln].filter(Boolean).join(" ") || user.name) : name}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">E-mail</div><div className="text-marine flex items-center gap-1.5"><Icons.Mail size={16} className="text-marine" /> {c.email || "—"}</div></div>
              <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Téléphone</div><div className="text-marine flex items-center gap-1.5"><Icons.Phone size={16} className="text-marine" /> {c.phone || "—"}</div></div>
              <div className="sm:col-span-2"><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Adresse postale</div><div className="text-marine">{c.address || "—"}</div></div>
              {isCollab && <>
                <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Pays</div><div className="text-marine">{c.country || "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Ville</div><div className="text-marine">{c.city || "—"}</div></div>
                <div><div className="text-xs uppercase tracking-wider text-marine/80 mb-1">Rayon de déplacement</div><div className="text-marine">{c.travel_radius !== "" && c.travel_radius != null ? `${c.travel_radius} km` : "—"}</div></div>
              </>}
            </div>
            {isCollab && (
              <div className="mt-6 pt-5 border-t border-marine/10" data-testid="profile-socials">
                <div className="flex items-center gap-2 mb-3"><h3 className="font-display text-lg text-marine">Réseaux sociaux</h3></div>
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: "instagram", label: "Instagram", icon: "Instagram" },
                    { key: "linkedin", label: "LinkedIn", icon: "Linkedin" },
                    { key: "facebook", label: "Facebook", icon: "Facebook" },
                    { key: "tiktok", label: "TikTok", icon: "Music2" },
                    { key: "youtube", label: "YouTube", icon: "Youtube" },
                    { key: "other", label: socials.other_label || "Autre", icon: "Link", value: socials.other_value },
                  ].filter(({ key, value }) => value !== undefined ? value : socials[key]).map(({ key, label, icon, value }) => {
                    const Ico = ICONS_BY_NAME[icon] || Icons.Link;
                    const v = value !== undefined ? value : socials[key];
                    const link = socialUrl(key, v);
                    const content = <><Ico size={16} /> {label}</>;
                    return link ? (
                      <a key={key} href={link} target="_blank" rel="noopener noreferrer" data-testid={`profile-social-view-${key}`}
                        className="flex items-center gap-1.5 border border-marine/15 rounded-sm px-3 py-1.5 text-sm text-marine hover:bg-marine-50 transition-colors">{content}</a>
                    ) : (
                      <span key={key} data-testid={`profile-social-view-${key}`} className="flex items-center gap-1.5 border border-marine/15 rounded-sm px-3 py-1.5 text-sm text-marine">{content}</span>
                    );
                  })}
                  {!socials.instagram && !socials.linkedin && !socials.facebook && !socials.tiktok && !socials.youtube && !socials.other_value &&
                    <p className="text-sm text-marine/80">Aucun réseau renseigné.</p>}
                </div>
              </div>
            )}
          </>
        ) : (
          <div data-testid="profile-edit-form" className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              {isCollab ? (
                <>
                  <label className="text-sm">Prénom<input value={fn} onChange={(e) => setFn(e.target.value)} data-testid="profile-first-name" className="inp mt-1" placeholder="Prénom" /></label>
                  <label className="text-sm">Nom<input value={ln} onChange={(e) => setLn(e.target.value)} data-testid="profile-last-name" className="inp mt-1" placeholder="Nom" /></label>
                </>
              ) : (
                <label className="text-sm">Nom<input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name" className="inp mt-1" /></label>
              )}
              <label className="text-sm">Adresse e-mail<input type="email" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} data-testid="profile-email" className="inp mt-1" /></label>
              <label className="text-sm">Numéro de téléphone<input value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} data-testid="profile-phone" className="inp mt-1" placeholder="+971 ..." /></label>
              <label className="text-sm">Adresse postale<input value={c.address} onChange={(e) => setC({ ...c, address: e.target.value })} data-testid="profile-address" className="inp mt-1" placeholder="Rue, ville, code postal" /></label>
              {isCollab && <>
                <label className="text-sm">Pays <span className="text-rouge">*</span><input value={c.country} onChange={(e) => setC({ ...c, country: e.target.value })} data-testid="profile-country" className="inp mt-1" placeholder="Émirats arabes unis" /></label>
                <label className="text-sm">Ville<input value={c.city} onChange={(e) => setC({ ...c, city: e.target.value })} data-testid="profile-city" className="inp mt-1" placeholder="Dubaï" /></label>
                <label className="text-sm">Rayon de déplacement (km)<input type="number" min="0" value={c.travel_radius} onChange={(e) => setC({ ...c, travel_radius: e.target.value })} data-testid="profile-radius" className="inp mt-1" placeholder="ex. 50" /></label>
              </>}
            </div>

            {isCollab && (
              <div className="mt-6 pt-5 border-t border-marine/10" data-testid="profile-socials-edit">
                <div className="flex items-center gap-2 mb-3"><h3 className="font-display text-lg text-marine">Réseaux sociaux</h3></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { key: "instagram", label: "Instagram", icon: "Instagram" },
                    { key: "linkedin", label: "LinkedIn", icon: "Linkedin" },
                    { key: "facebook", label: "Facebook", icon: "Facebook" },
                    { key: "tiktok", label: "TikTok", icon: "Music2" },
                    { key: "youtube", label: "YouTube", icon: "Youtube" },
                  ].map(({ key, label, icon }) => {
                    const Ico = ICONS_BY_NAME[icon] || Icons.Link;
                    const link = socialUrl(key, socials[key]);
                    return (
                      <label key={key} className="text-sm">
                        <span className="flex items-center gap-1.5 mb-1"><Ico size={16} className="text-marine" /> {label}</span>
                        <div className="flex items-center gap-1.5">
                          <input value={socials[key]} onChange={(e) => setSocials({ ...socials, [key]: e.target.value })} data-testid={`profile-social-${key}`} className="inp flex-1" placeholder="@profil ou lien" />
                          {link && <a href={link} target="_blank" rel="noopener noreferrer" className="shrink-0 w-9 h-9 rounded-sm bg-sable-clair text-marine flex items-center justify-center hover:bg-sable"><Icons.ExternalLink size={16} /></a>}
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div className="mt-4">
                  <span className="flex items-center gap-1.5 mb-1 text-sm"><Icons.Link size={16} className="text-marine" /> Autre</span>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input value={socials.other_label} onChange={(e) => setSocials({ ...socials, other_label: e.target.value })} data-testid="profile-social-other-label" className="inp" placeholder="Nom de la plateforme" />
                    <div className="flex items-center gap-1.5">
                      <input value={socials.other_value} onChange={(e) => setSocials({ ...socials, other_value: e.target.value })} data-testid="profile-social-other-value" className="inp flex-1" placeholder="Lien ou identifiant" />
                      {socialUrl("other", socials.other_value) && <a href={socialUrl("other", socials.other_value)} target="_blank" rel="noopener noreferrer" className="shrink-0 w-9 h-9 rounded-sm bg-sable-clair text-marine flex items-center justify-center hover:bg-sable"><Icons.ExternalLink size={16} /></a>}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button onClick={() => setEditP(false)} className="px-4 py-2 text-sm text-marine/80">Annuler</button>
              <button onClick={save} disabled={savingP} data-testid="profile-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover transition-colors disabled:opacity-50">{savingP ? "Enregistrement…" : "Enregistrer"}</button>
            </div>
          </div>
        )}
      </div>

      {isCollab && (
        <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-6" data-testid="profile-password">
          <div className="flex items-center gap-2 mb-4"><h2 className="font-display text-xl text-marine">Changer le mot de passe</h2></div>
          <form onSubmit={submitPwd} className="space-y-4 max-w-md">
            <label className="text-sm block">Mot de passe actuel<input type="password" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} data-testid="profile-current-password" className="inp mt-1" required /></label>
            <label className="text-sm block">Nouveau mot de passe<input type="password" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} data-testid="profile-new-password" className="inp mt-1" required /></label>
            <PasswordRules value={pwd.new_password} />
            <label className="text-sm block">Confirmer le nouveau mot de passe<input type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} data-testid="profile-confirm-password" className="inp mt-1" required /></label>
            <div className="flex justify-end"><button disabled={savingPwd} data-testid="profile-password-save" className="bg-marine text-white rounded-sm px-5 py-2 text-sm hover:bg-marine-hover transition-colors disabled:opacity-50">{savingPwd ? "Enregistrement…" : "Mettre à jour"}</button></div>
          </form>
        </div>
      )}

      {isCollab && (
        <div className="bg-sable rounded-sm border border-marine/10 shadow-sm p-6 mt-8" data-testid="profile-data">
          <div className="flex items-center gap-2 mb-4"><h2 className="font-display text-xl text-marine">Mes données</h2></div>
          <p className="text-sm text-marine/80 mb-4 max-w-prose">Téléchargez une copie de vos données : votre fiche, vos missions (phases, fichiers, conversation) et leurs paiements, vos demandes à l'agence et la liste des fichiers que vous avez déposés sur les projets. Les fichiers eux-mêmes se téléchargent depuis votre espace.</p>
          <button onClick={exportData} disabled={exporting} data-testid="profile-export-data"
            className="text-sm border border-marine/25 text-marine rounded-sm px-4 py-2 hover:bg-marine hover:text-white transition-colors disabled:opacity-50 flex items-center gap-1.5">
            <Icons.Download size={16} /> {exporting ? "Préparation…" : "Télécharger mes données"}
          </button>
        </div>
      )}
    </div>
  );
}
